import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAppStore } from '../../contexts/store';
import {
  CalendarDays,
  Home,
  ListChecks,
  Loader2,
  Map,
  MessageSquare,
  User,
  Wifi,
  WifiOff,
  RefreshCw,
} from 'lucide-react';
import { Suspense, useState, useEffect } from 'react';
import { Network, type ConnectionStatus } from '@capacitor/network';

export function FieldLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated } = useAppStore();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingSyncCount] = useState(0); // TODO: Connect to sync service

  // Online/offline detection. @capacitor/network proxies to navigator.onLine
  // on web and uses the OS reachability APIs on native (more reliable than
  // the browser heuristic — correctly detects "Wi-Fi but no internet" /
  // captive portals where window.onLine would falsely report online).
  useEffect(() => {
    let listener: { remove: () => Promise<void> } | undefined;
    let cancelled = false;

    Network.getStatus()
      .then((status: ConnectionStatus) => {
        if (!cancelled) setIsOnline(status.connected);
      })
      .catch(() => undefined);

    Network.addListener('networkStatusChange', (status: ConnectionStatus) => {
      if (!cancelled) setIsOnline(status.connected);
    })
      .then((handle) => {
        if (cancelled) {
          handle.remove();
        } else {
          listener = handle;
        }
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
      listener?.remove().catch(() => undefined);
    };
  }, []);

  // Redirect if not authenticated or no field access
  useEffect(() => {
    if (!isAuthenticated) {
      // Pass current location so LoginPage can redirect back after auth
      navigate('/login', { state: { from: location }, replace: true });
    } else if (user && user.hasFieldAppAccess === false) {
      // User doesn't have field app access, redirect to dashboard
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, navigate, location]);

  const navItems = [
    { path: '/field', label: 'Today', icon: Home, end: true },
    { path: '/field/work', label: 'Work', icon: ListChecks },
    { path: '/field/schedule', label: 'Schedule', icon: CalendarDays },
    { path: '/field/messages', label: 'Messages', icon: MessageSquare },
    { path: '/field/map', label: 'Map', icon: Map },
    { path: '/field/profile', label: 'Profile', icon: User },
  ];

  return (
    /* Fixed-height column so the header and bottom nav anchor at the
       viewport edges and only the <main> region scrolls. min-h-screen
       would let the whole div grow taller than the viewport on long
       pages, causing the document to scroll and dragging the header
       off-screen. h-dvh tracks the dynamic viewport (handles iOS WebView
       toolbar visibility on real Safari, no-op inside Capacitor's
       fixed-viewport WKWebView). */
    <div className="h-dvh bg-gray-50 dark:bg-gray-900 flex flex-col overflow-hidden">
      {/* Minimal Header — flex-shrink-0 keeps it from shrinking under content pressure */}
      <header className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center justify-between safe-area-top">
        <div className="flex items-center gap-2">
          {/* App icon in the bundle — keeps brand visible without depending
              on a tenant logo (the field app is brand-Dispatch, not tenant-branded). */}
          <img
            src="/icons/icon-48.webp"
            alt=""
            aria-hidden="true"
            className="w-6 h-6 rounded"
          />
          <span className="font-semibold text-gray-900 dark:text-white">
            Dispatch
          </span>
          {user && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {user.firstName}
            </span>
          )}
        </div>

        {/* Sync Status Indicator */}
        <div className="flex items-center gap-2">
          {pendingSyncCount > 0 && (
            <div
              className="flex items-center gap-1 text-amber-600 dark:text-amber-400"
              role="status"
              aria-label={`${pendingSyncCount} change${
                pendingSyncCount === 1 ? '' : 's'
              } waiting to sync`}
            >
              <RefreshCw className="w-4 h-4 animate-spin" aria-hidden="true" />
              <span className="text-xs font-medium" aria-hidden="true">
                {pendingSyncCount}
              </span>
            </div>
          )}
          {isOnline ? (
            <>
              <Wifi className="w-5 h-5 text-green-500" aria-hidden="true" />
              <span className="sr-only">Online</span>
            </>
          ) : (
            <>
              <WifiOff className="w-5 h-5 text-red-500" aria-hidden="true" />
              <span className="sr-only">Offline</span>
            </>
          )}
        </div>
      </header>

      {/* Offline Banner. aria-live="polite" announces the state change
          (and a one-time read on mount when already offline). Sighted
          users see the amber bar; SR users hear "You're offline…" without
          needing to navigate to the header icon. */}
      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          className="bg-amber-500 text-white text-center text-sm py-1 px-4"
        >
          You're offline. Changes will sync when connected.
        </div>
      )}

      {/* Main Content Area — the only scrollable region.
          pb-20 was previously needed to clear the fixed bottom nav; with
          the nav now a sibling in the flex column, the layout reserves
          its space naturally and the padding is no longer required. */}
      <main className="flex-1 overflow-y-auto overflow-x-hidden">
        {/* Nested Suspense so a lazy page chunk loading inside /field
            doesn't unmount the header + bottom nav. The top-level App
            Suspense would replace the whole layout with a full-screen
            spinner on every tab switch, which is jarring. */}
        <Suspense
          fallback={
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" aria-hidden="true" />
              <span className="sr-only">Loading…</span>
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </main>

      {/* Bottom Navigation — part of the flex column, anchored at the
          bottom by the column layout itself instead of position:fixed.
          Simpler stacking, no z-index dance, and content can no longer
          render under it. */}
      <nav className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 safe-area-bottom">
        <div className="flex justify-around items-center h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center w-full h-full min-h-[44px] transition-colors ${
                  isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400'
                }`
              }
            >
              <item.icon className="w-6 h-6" />
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

export default FieldLayout;
